import { useMemo } from "react";
import { GLYPH_PATHS, FALLBACK_GLYPHS } from "./constants";

/* Animated zodiac badge — a circular celestial seal (distinct from the Mayan
   square cartouche and the Chinese square chop). A slowly rotating tick ring
   around an inner circle, with the sign's glyph at center. Used for Western
   (sun sign, cyan) and Vedic (moon sign, amber). Doubles as a home-web
   satellite node like Stand Star.

   sign: lowercase sign key ("aries".."pisces").
   Modes: outline (dim, un-activated) | default (lit, slow spin + glow pulse). */

const INK = "#E4DED4";

function ZodiacBadge({ sign, accent, size = 150, outline = false, bare = false }) {
  const uid = useMemo(() => Math.random().toString(36).slice(2, 8), []);
  const lit = !outline;
  const frameOp = lit ? 1 : 0.45;
  const paths = GLYPH_PATHS[sign];

  // Bare: just the sign glyph (no circle/ring), accent-colored, gentle glow.
  if (bare) {
    return (
      <svg viewBox="0 0 150 150" width={size} height={size} style={{ display: "block", margin: "0 auto", overflow: "visible" }}>
        <defs>
          <filter id={`zdg-b-${uid}`} x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="1.4" result="b">
              <animate attributeName="stdDeviation" values="1;2.4;1" dur="4.5s" repeatCount="indefinite" />
            </feGaussianBlur>
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <g filter={`url(#zdg-b-${uid})`} transform="translate(15,15) scale(5)">
          {paths
            ? paths.map((p, i) => (p.d ? <path key={i} d={p.d} stroke={accent} strokeWidth="2.4"
                strokeLinecap="round" strokeLinejoin="round" fill={p.fill ? accent : "none"}
                opacity={p.op || 0.95} vectorEffect="non-scaling-stroke" /> : null))
            : <text x="12" y="13" textAnchor="middle" dominantBaseline="central" fontSize="16" fill={accent}>{FALLBACK_GLYPHS[sign] || "?"}</text>}
        </g>
      </svg>
    );
  }

  const css = `
    .zdg-spin-${uid}{transform-box:fill-box;transform-origin:center;animation:zdg-spin-${uid} 95s linear infinite;}
    @keyframes zdg-spin-${uid}{to{transform:rotate(360deg);}}
  `;
  const ticks = Array.from({ length: 12 }, (_, i) => (i / 12) * Math.PI * 2);

  return (
    <svg viewBox="0 0 150 150" width={size} height={size} style={{ display: "block", margin: "0 auto", overflow: "visible" }}>
      <defs>
        <filter id={`zdg-glow-${uid}`} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="2" result="b">
            {lit && <animate attributeName="stdDeviation" values="1.5;3.2;1.5" dur="4.5s" repeatCount="indefinite" />}
          </feGaussianBlur>
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <radialGradient id={`zdg-shade-${uid}`} cx="50%" cy="38%" r="70%">
          <stop offset="60%" stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.3" />
        </radialGradient>
      </defs>
      <style>{css}</style>

      <g filter={lit ? `url(#zdg-glow-${uid})` : undefined}>
        {/* rotating zodiac tick ring (12 ticks) */}
        <g className={`zdg-spin-${uid}`} opacity={lit ? 0.65 : 0.3}>
          <circle cx="75" cy="75" r="70" fill="none" stroke={accent} strokeWidth="1" opacity="0.5" />
          {ticks.map((a, i) => (
            <line key={i} x1={75 + Math.cos(a) * 64} y1={75 + Math.sin(a) * 64}
              x2={75 + Math.cos(a) * 70} y2={75 + Math.sin(a) * 70}
              stroke={accent} strokeWidth="2" opacity="0.85" />
          ))}
        </g>

        {/* inner circle seal */}
        <circle cx="75" cy="75" r="52" fill="#1A1C2E" stroke={accent} strokeWidth="2.4" opacity={frameOp} />
        <circle cx="75" cy="75" r="44" fill="none" stroke={INK} strokeWidth="0.8" opacity={lit ? 0.3 : 0.18} />
        <circle cx="75" cy="75" r="52" fill={`url(#zdg-shade-${uid})`} />

        {/* sign glyph, centered and scaled up to fill */}
        <g transform="translate(36,36) scale(3.25)" opacity={lit ? 0.95 : 0.5}>
          {paths ? (
            paths.map((p, i) => (
              p.d ? <path key={i} d={p.d} stroke={INK} strokeWidth="0.85" strokeLinecap="round" strokeLinejoin="round"
                fill={p.fill ? INK : "none"} opacity={p.op || 0.95} vectorEffect="non-scaling-stroke" /> : null
            ))
          ) : (
            <text x="12" y="13" textAnchor="middle" dominantBaseline="central" fontSize="16" fill={INK}>{FALLBACK_GLYPHS[sign] || "?"}</text>
          )}
        </g>
      </g>
    </svg>
  );
}

export function WesternGlyph({ sign, size = 150, outline = false, bare = false }) {
  return <ZodiacBadge sign={sign} accent="#68B8C8" size={size} outline={outline} bare={bare} />;
}
export function VedicGlyph({ sign, size = 150, outline = false, bare = false }) {
  return <ZodiacBadge sign={sign} accent="#D4A574" size={size} outline={outline} bare={bare} />;
}
